import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Checkbox, Modal } from 'antd'

const preferenceRequest = async (method = 'GET') => {
  const token = JSON.parse(localStorage.getItem('session') || 'null')?.accessToken
  if (!token) throw new Error('Please sign in to save your preference.')
  const response = await fetch(`${process.env.REACT_APP_DEV_API}/user-preferences/import-notice`, {
    method,
    headers: { Authorization: `Bearer ${token}`, ...(method === 'PUT' ? { 'Content-Type': 'application/json' } : {}) },
    ...(method === 'PUT' ? { body: JSON.stringify({ hideImportNotice: true }) } : {}),
  })
  if (!response.ok) throw new Error('Your preference could not be saved. Please try again.')
  return response.json()
}

// Shown once after each successful PDF/Final Draft import. Continue stores the
// imported screenplay, then opens Scene Cards, which reads the saved screenplay.
export default function ImportCompleteNotice({ completion, scriptId, saveScreenplay }) {
  const [open, setOpen] = useState(false)
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    if (!completion) return
    let active = true
    setDontShowAgain(false)
    setError('')
    // Read afresh after every import to honor another device's opt-out.
    preferenceRequest().then(preference => {
      if (active) setOpen(preference.hideImportNotice !== true)
    }).catch(() => {
      // Failed reads must never imply the account opted out.
      if (active) setOpen(true)
    })
    return () => { active = false }
  }, [completion])

  const acknowledge = async () => {
    setSaving(true)
    setError('')
    try {
      if (dontShowAgain) {
        const preference = await preferenceRequest('PUT')
        if (preference.hideImportNotice !== true) throw new Error('Your preference could not be saved. Please try again.')
      }
      if (saveScreenplay) await saveScreenplay()
      setOpen(false)
      if (scriptId) navigate(`/scripts/${scriptId}/scenecards`)
    } catch (failure) { setError(failure.message) }
    finally { setSaving(false) }
  }

  return (
    <Modal title="Script Formatting" open={open} centered width={460} closable={false}
      maskClosable={false} keyboard={false}
      footer={<Button type="primary" loading={saving} onClick={acknowledge}>Continue</Button>}>
      <div className="flex flex-col gap-4 py-2">
        <p>Different screenplay formats, particularly PDFs, may not always convert perfectly. Some scene headings, character names, dialogue, or spacing may need checking.</p>
        <Checkbox checked={dontShowAgain} disabled={saving} onChange={event => setDontShowAgain(event.target.checked)}>
          Don't show me again
        </Checkbox>
        {error && <Alert type="error" showIcon message={error} />}
      </div>
    </Modal>
  )
}
